import { BusyButton } from "@/components/acquisitions/BusyButton";
import { BinderyItemStatusBadge } from "@/components/bindery/BinderyStatusBadge";
import { DAMAGE_TYPE_LABELS, REPAIR_TYPE_LABELS, formatMoney } from "../binderyFormatters";
import type { BinderyRequestItem } from "@/lib/api/bindery";

type QcOutcome = "READY_TO_SHELVE" | "RETURNED_UNREPAIRED";

export function binderyQcPendingKey(itemRowId: string, outcome: QcOutcome) {
  return `qc:${itemRowId}:${outcome}`;
}

interface Props {
  items: BinderyRequestItem[];
  requestStatus: string;
  onQc: (itemRowId: string, outcome: QcOutcome) => void;
  pendingKey: string | null;
}

export function BinderyRequestItemsTable({ items, requestStatus, onQc, pendingKey }: Props) {
  const showQcActions = requestStatus === "RETURNED";
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">Item</th>
            <th className="px-4 py-2 text-left">Damage / Repair</th>
            <th className="px-4 py-2 text-right">Cost</th>
            <th className="px-4 py-2 text-left">Status</th>
            {showQcActions && <th className="px-4 py-2 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((ri) => (
            <tr key={ri.id} className="align-top">
              <td className="px-4 py-3">
                <p className="font-mono text-xs text-slate-600">{ri.item.barcode}</p>
                <p className="font-medium text-slate-900">{ri.item.biblio.title}</p>
                {ri.item.biblio.authors?.[0] && (
                  <p className="text-xs text-slate-500">{ri.item.biblio.authors[0]}</p>
                )}
              </td>
              <td className="px-4 py-3 text-slate-700">
                <p className="text-xs">
                  <span className="text-slate-500">Damage:</span>{" "}
                  {ri.damageType ? DAMAGE_TYPE_LABELS[ri.damageType] : "—"}
                </p>
                <p className="text-xs">
                  <span className="text-slate-500">Repair:</span>{" "}
                  {ri.repairType ? REPAIR_TYPE_LABELS[ri.repairType] : "—"}
                </p>
                {ri.instructions && (
                  <p className="mt-1 text-xs italic text-slate-500">{ri.instructions}</p>
                )}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{formatMoney(ri.cost)}</td>
              <td className="px-4 py-3">
                <BinderyItemStatusBadge status={ri.status} />
              </td>
              {showQcActions && (
                <td className="px-4 py-3 text-right">
                  {ri.status === "QC_PENDING" ? (
                    <div className="flex flex-wrap justify-end gap-2">
                      <BusyButton
                        size="sm"
                        busy={pendingKey === binderyQcPendingKey(ri.id, "READY_TO_SHELVE")}
                        disabled={pendingKey !== null}
                        onClick={() => onQc(ri.id, "READY_TO_SHELVE")}
                      >
                        Pass QC
                      </BusyButton>
                      <BusyButton
                        size="sm"
                        variant="outline"
                        busy={pendingKey === binderyQcPendingKey(ri.id, "RETURNED_UNREPAIRED")}
                        disabled={pendingKey !== null}
                        onClick={() => onQc(ri.id, "RETURNED_UNREPAIRED")}
                      >
                        Fail QC
                      </BusyButton>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
